import { ref, computed, watch } from '@vue/composition-api'
import { apiToastErrorV2 } from '@/@core/utils/toast'
import { getWishlistTitle } from '@/@core/utils/utils'
import constants, { marketplaceAccessDeniedMessage, TOOLKIT_PAGE } from '@/constants'
import store from '@/store'
import { FETCH_DIRECT_RETAILERS } from '@/store/modules/connections.module'
import { FETCH_WISHLISTS } from '@/store/modules/shop.module'
import { constants as c } from '@kingpin-global/kingpin-utils-frontend'
import { getSelfOption } from '@/service/user.service'
import { RESET_WISHLIST_NAV_INDICATOR } from '@/store/modules/notification.module'
import {
  RESET_SELECTED_SELECTIONS,
  SELECT_ALL_SELECTIONS,
  SELECT_OR_REMOVE_SELETION,
  shareSelectionsErrorModalId,
} from '@/store/modules/multi-select.module'
import analytics from '@/@core/utils/analytics'

const { BRAND_ACCESS } = c
const { SHARE_MULTIPLE_SELECTIONS } = constants.TRACKS.ACTIONS

export function useWishlistList() {
  // Reactive state
  const isLoaded = ref(false)
  const search = ref('')
  const fullWishlists = ref([])
  const rawWishlists = ref([])
  const rawWishlistsRetailer = ref([])
  const rawWishlistsBrand = ref([])
  const wishlists = ref([])
  const wishlistsBrand = ref([])
  const wishlistsRetailer = ref([])
  const isSearching = ref(false)
  const tabs = ref(0)
  const selectedRepsNames = ref([])
  const selectedSalesManagerNames = ref([])
  const selectedCollections = ref([])
  const selectedRetailers = ref([])
  const wishlistCollections = ref([])
  const wishlistRetailers = ref([])
  const resetKey = ref(0)
  const shareSelectionResponseMessages = ref([])
  const referralEmail = ref('')
  const referralWishlistId = ref('')
  const selectedWishlist = ref({})
  const retailerName = ref('')
  const retailerEmail = ref('')
  let debounce = null

  // Store-derived computed
  const profile = computed(() => store.state.auth.profileData)
  const salesReps = computed(() => store.state.auth.salesReps)
  const salesManagers = computed(() => store.state.auth.salesManagers)
  const selectedSelections = computed(() => store.getters.getSelectedSelections || [])
  const isSelectedMultipleSelections = computed(() => !!selectedSelections.value.length)
  const isEnabledMarketplace = computed(() => profile.value?.access?.includes(BRAND_ACCESS.MARKETPLACE))

  // Role computed (used internally; components get these from UserRoleMixin)
  const isRetailer = computed(() => store.getters.isRetailer)

  const canShowSearch = computed(() => {
    const isSearchingNow = search.value || isSearching.value
    return fullWishlists.value.length || isSearchingNow
  })

  // Methods
  function setFilterOptions() {
    const uniqueRetailerIds = new Set()
    const uniqueCollectionIds = new Set()

    const source = isRetailer.value ? rawWishlistsBrand.value : rawWishlistsRetailer.value

    source.forEach((wishlist, index) => {
      if (wishlist.retailerName && !uniqueRetailerIds.has(wishlist.retailerName)) {
        wishlistRetailers.value.push({
          userId: `${wishlist.retailerName}${index}`,
          name: wishlist.retailerName,
        })
        uniqueRetailerIds.add(wishlist.retailerName)
      }
      if (wishlist.collectionName && !uniqueCollectionIds.has(wishlist.collectionName)) {
        wishlistCollections.value.push({
          userId: `${wishlist.collectionName}${index}`,
          name: wishlist.collectionName,
        })
        uniqueCollectionIds.add(wishlist.collectionName)
      }
    })
  }

  function fetchWishlists(searchQuery = '') {
    store
      .dispatch(FETCH_WISHLISTS, { force: true, search: searchQuery })
      .then(fetchedWishlists => {
        fullWishlists.value = fetchedWishlists
        if (isRetailer.value) {
          wishlists.value = fetchedWishlists.filter(
            w => !w?.isBrandInitiated,
          )
          wishlistsBrand.value = fetchedWishlists.filter(
            w => w?.isBrandInitiated,
          )
          rawWishlistsBrand.value = wishlistsBrand.value
        } else {
          wishlists.value = fetchedWishlists.filter(
            w => !w?.isReadOnly,
          )
          wishlistsRetailer.value = fetchedWishlists.filter(
            w => w?.isReadOnly,
          )
        }
        rawWishlists.value = wishlists.value
        rawWishlistsRetailer.value = wishlistsRetailer.value
        if (!isLoaded.value) {
          setFilterOptions()
        }
        isLoaded.value = true
        isSearching.value = false
      })
      .catch(err => {
        isLoaded.value = true
        isSearching.value = false
        apiToastErrorV2(err)
      })
  }

  function searchWishlist() {
    isSearching.value = true
    clearTimeout(debounce)
    debounce = setTimeout(() => {
      fetchWishlists(search.value)
    }, 500)
  }

  function noFiltersSelected(selectedNames, selectedCollectionAndRetailerNames) {
    return selectedNames.length === 0 && selectedCollectionAndRetailerNames.length === 0
  }

  function resetFilters() {
    wishlists.value = rawWishlists.value
    wishlistsRetailer.value = rawWishlistsRetailer.value
    wishlistsBrand.value = rawWishlistsBrand.value
    resetKey.value += 1
  }

  function applySalesRepFilter(selectedNames, isSelectedSelf) {
    wishlists.value = rawWishlists.value.filter(
      w => selectedNames.includes(w.salesRep) || (isSelectedSelf && w.isCreatedBySelf),
    )
  }

  function applyCollectionAndRetailerFilter(selectedNames, selectedCollectionAndRetailerNames) {
    const filterFn = w => selectedNames.includes(w.salesRep)
      || selectedCollectionAndRetailerNames.includes(w?.collectionName)
      || selectedCollectionAndRetailerNames.includes(w?.retailerName)

    if (isRetailer.value) {
      wishlistsBrand.value = rawWishlistsBrand.value.filter(filterFn)
    } else {
      wishlistsRetailer.value = rawWishlistsRetailer.value.filter(filterFn)
    }
  }

  function updateFilter() {
    const selectedNames = [...selectedRepsNames.value, ...selectedSalesManagerNames.value]
    const selectedCollectionAndRetailerNames = [...selectedCollections.value, ...selectedRetailers.value]

    if (noFiltersSelected(selectedNames, selectedCollectionAndRetailerNames)) {
      resetFilters()
      return
    }

    const isSelectedSelf = selectedNames.includes(getSelfOption().name)
    applySalesRepFilter(selectedNames, isSelectedSelf)
    applyCollectionAndRetailerFilter(selectedNames, selectedCollectionAndRetailerNames)
  }

  function onFilterBySalesRep(selectedValues = []) {
    selectedRepsNames.value = selectedValues.map(s => s.name)
    updateFilter()
  }

  function onFilterBySalesManager(selectedValues = []) {
    selectedSalesManagerNames.value = selectedValues.map(s => s.name)
    updateFilter()
  }

  function onFilterByCollection(selectedValues = []) {
    selectedCollections.value = selectedValues.map(s => s.name)
    updateFilter()
  }

  function onFilterByRetailer(selectedValues = []) {
    selectedRetailers.value = selectedValues.map(s => s.name)
    updateFilter()
  }

  function selectAllSelections() {
    const selectedSelectionIds = wishlists.value.map(w => w._id)
    store.commit(SELECT_ALL_SELECTIONS, selectedSelectionIds)
    analytics.track(SHARE_MULTIPLE_SELECTIONS.BRNAD_CLICKS_SELECT_ALL_SELECTIONS, { selectedSelectionIds })
  }

  function resetSelectedSelections() {
    store.commit(RESET_SELECTED_SELECTIONS)
  }

  function onSelectSelection(wishlist) {
    store.commit(SELECT_OR_REMOVE_SELETION, wishlist)
  }

  function openReferralModel(email, wishlist) {
    selectedWishlist.value = wishlist
    referralEmail.value = email
    referralWishlistId.value = wishlist._id
  }

  function openWishlistNotification(name, email) {
    retailerEmail.value = email
    retailerName.value = name
  }

  function openShareSelectionsErrorModal(messages) {
    shareSelectionResponseMessages.value = []
    shareSelectionResponseMessages.value = messages
    analytics.track(SHARE_MULTIPLE_SELECTIONS.SELECTIONS_SHARING_RESULT_POPUP_OPENED, { shareSelectionsResponseMessages: messages })
  }

  // Init and destroy
  function initWishlistList() {
    store.commit(RESET_WISHLIST_NAV_INDICATOR)
    store.dispatch(FETCH_DIRECT_RETAILERS)
    fetchWishlists()
    if (isRetailer.value) {
      analytics.track(constants.TRACKS.PAGES.RETAILER_OPENS_WISHLISTS)
    } else {
      analytics.track(constants.TRACKS.PAGES.BRAND_OPENS_SELECTION)
    }
  }

  function destroyWishlistList() {
    resetSelectedSelections()
  }

  // Tab watch
  watch(tabs, val => {
    if (search.value) {
      search.value = ''
      fetchWishlists(search.value)
    } else {
      resetFilters()
    }
    if (val === 1) {
      resetSelectedSelections()
    }
  })

  return {
    // State
    isLoaded,
    search,
    fullWishlists,
    rawWishlists,
    rawWishlistsRetailer,
    rawWishlistsBrand,
    wishlists,
    wishlistsBrand,
    wishlistsRetailer,
    isSearching,
    tabs,
    selectedRepsNames,
    selectedSalesManagerNames,
    selectedCollections,
    selectedRetailers,
    wishlistCollections,
    wishlistRetailers,
    resetKey,
    shareSelectionResponseMessages,
    referralEmail,
    referralWishlistId,
    selectedWishlist,
    retailerName,
    retailerEmail,

    // Computed
    profile,
    salesReps,
    salesManagers,
    selectedSelections,
    isSelectedMultipleSelections,
    isEnabledMarketplace,
    canShowSearch,

    // Methods
    fetchWishlists,
    searchWishlist,
    setFilterOptions,
    onFilterBySalesRep,
    onFilterBySalesManager,
    onFilterByCollection,
    onFilterByRetailer,
    updateFilter,
    resetFilters,
    selectAllSelections,
    resetSelectedSelections,
    onSelectSelection,
    openReferralModel,
    openWishlistNotification,
    openShareSelectionsErrorModal,

    // Lifecycle
    initWishlistList,
    destroyWishlistList,

    // Constants
    getWishlistTitle,
    marketplaceAccessDeniedMessage,
    TOOLKIT_PAGE,
    shareSelectionsErrorModalId,
  }
}
